import { zodResolver } from "@hookform/resolvers/zod";
import { addDays } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";

const raffleFormSchema = z
  .object({
    identificadorSorteioStart: z.string().optional().default(""),
    name: z.string().min(1, { message: "Nome é obrigatório" }),
    description: z.string().min(1, { message: "Descrição é obrigatória" }),
    endDate: z.date({ required_error: "Data de término é obrigatória" }),
    startDate: z.date({ required_error: "Data de início é obrigatória" }),
    resultDate: z.date({ required_error: "Data de resultado é obrigatória" }),
    banner: z
      .instanceof(File)
      .nullable()
      .refine((file) => file && file.size > 0, {
        message: "Capa do sorteio é obrigatória",
      }),
    gallery: z.array(z.instanceof(File)).nullable(),
    couponValue: z
      .number()
      .min(0.01, { message: "Valor deve ser maior que zero" }),
    couponCode: z.string().optional(),
    prizes: z
      .array(
        z.object({
          name: z.string().min(1, { message: "Nome do prêmio é obrigatório" }),
        }),
      )
      .min(1, { message: "Deve haver pelo menos um prêmio" }),
  })
  .superRefine((data, ctx) => {
    const { startDate, resultDate, endDate } = data;

    if (startDate >= resultDate) {
      ctx.addIssue({
        code: "custom",
        path: ["resultDate"],
        message: "Data de resultado deve ser maior que a data de início",
      });
    }

    if (startDate >= endDate) {
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: "Data de término deve ser maior que a data de início",
      });
    }

    if (endDate >= resultDate) {
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: "Data de término deve ser menor que a data de resultado",
      });
    }
  });

export type RaffleFormValues = z.infer<typeof raffleFormSchema>;

const initialValues: RaffleFormValues = {
  identificadorSorteioStart: "",
  name: "",
  description: "",
  startDate: new Date(),
  endDate: addDays(new Date(), 1),
  resultDate: addDays(new Date(), 2),
  banner: null,
  gallery: null,
  couponValue: 10,
  couponCode: "",
  prizes: [{ name: "Prêmio 1" }],
};

interface UseRaffleFormProps {
  getDefaultValues?: () => Promise<RaffleFormValues>;
}

export const useRaffleForm = ({ getDefaultValues }: UseRaffleFormProps) => {
  return useForm<RaffleFormValues>({
    resolver: zodResolver(raffleFormSchema),
    defaultValues: async () => {
      if (getDefaultValues) {
        return await getDefaultValues();
      }
      return initialValues;
    },
  });
};
